// src/pages/app/ExpenseClaimsPage.jsx — Expense claims list & management
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { expenseClaimsApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Search, Eye, CheckCircle2, XCircle, Banknote, DollarSign, Receipt } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  pending_approval: 'bg-amber-100 text-amber-800',
  manager_approved: 'bg-teal-100 text-teal-800',
  finance_approved: 'bg-blue-100 text-blue-800',
  settled: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'manager_approved', label: 'Manager Approved' },
  { value: 'settled', label: 'Settled' },
  { value: 'rejected', label: 'Rejected' },
];

export default function ExpenseClaimsPage() {
  const { isRole } = useAuth();
  const [searchParams] = useSearchParams();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [actionLoading, setActionLoading] = useState({});

  const [approveModal, setApproveModal] = useState({ open: false, id: null, comment: 'Approved' });
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, comment: '' });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await expenseClaimsApi.list({ status: statusFilter || undefined });
      setClaims(data);
    } catch { toast.error('Failed to load expense claims'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const handleApproveSubmit = async () => {
    if (!approveModal.id) return;
    const id = approveModal.id;
    setActionLoading(p => ({ ...p, [id]: 'approving' }));
    try {
      await expenseClaimsApi.approve(id, { comment: approveModal.comment || 'Approved' });
      toast.success('Expense claim approved!');
      setApproveModal({ open: false, id: null, comment: 'Approved' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
    finally { setActionLoading(p => ({ ...p, [id]: null })); }
  };

  const [settleId, setSettleId] = useState(null);
  const [showSettleModal, setShowSettleModal] = useState(false);

  const handleSettleClick = (id) => {
    setSettleId(id);
    setShowSettleModal(true);
  };

  const handleConfirmSettle = async () => {
    if (!settleId) return;
    setActionLoading(p => ({ ...p, [settleId]: 'settling' }));
    try {
      await expenseClaimsApi.settle(settleId, { comment: 'Settled via CTEMS' });
      toast.success('Claim settled! Employee notified.');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Settlement failed'); }
    finally {
      setActionLoading(p => ({ ...p, [settleId]: null }));
      setShowSettleModal(false);
      setSettleId(null);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectModal.id || !rejectModal.comment.trim() || rejectModal.comment.trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    const id = rejectModal.id;
    setActionLoading(p => ({ ...p, [id]: 'rejecting' }));
    try {
      await expenseClaimsApi.reject(id, { comment: rejectModal.comment });
      toast.success('Claim rejected');
      setRejectModal({ open: false, id: null, comment: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rejection failed'); }
    finally { setActionLoading(p => ({ ...p, [id]: null })); }
  };

  const filtered = claims.filter(c =>
    c.title?.toLowerCase().includes(search.toLowerCase()) ||
    c.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.trip_destination?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">Expense Claims</h1>
          <p className="text-slate-500 text-sm mt-1">Post-travel expense submission and settlement</p>
        </div>
        {isRole('employee', 'admin') && (
          <Link to="/app/expense-claims/new"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4" /> New Claim
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search claims..."
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <Select value={statusFilter || 'all'} onValueChange={val => setStatusFilter(val === 'all' ? '' : val)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(o => (
              <SelectItem key={o.value || 'all'} value={o.value || 'all'}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200 bg-white overflow-hidden shadow-sm">
        <div className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between text-xs text-slate-500 font-medium">
          <span>Showing <strong>{filtered.length}</strong> expense claim{filtered.length === 1 ? '' : 's'}</span>
        </div>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : !filtered.length ? (
            <div className="text-center py-16">
              <p className="text-slate-400 text-sm">No expense claims found</p>
              {isRole('employee', 'admin') && (
                <Link to="/app/expense-claims/new" className="mt-3 inline-block text-blue-600 text-sm font-semibold hover:underline">
                  Create your first claim →
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Claim Details</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Employee</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Trip Destination</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Total Claimed</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Net Reimbursable</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Status</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map(claim => (
                    <tr key={claim.id} className="hover:bg-slate-50/80 transition-colors group">
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                            <Receipt className="w-4 h-4" />
                          </div>
                          <div>
                            <span className="font-semibold text-slate-900 block group-hover:text-blue-600 transition-colors">{claim.title}</span>
                            <span className="text-xs text-slate-500">ID: #{claim.id}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 font-bold flex items-center justify-center text-[10px]">
                            {claim.employee_name?.charAt(0) || 'E'}
                          </div>
                          <span className="text-xs font-medium text-slate-700">{claim.employee_name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-600 font-medium">{claim.trip_destination || '—'}</td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-medium text-slate-700 whitespace-nowrap">
                        ₹{parseFloat(claim.total_amount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-emerald-700 whitespace-nowrap">
                        ₹{parseFloat(claim.net_reimbursable || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 whitespace-nowrap">
                        <span className={cn("inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium border capitalize", STATUS_COLORS[claim.status])}>
                          <span className={cn("w-1.5 h-1.5 rounded-full",
                            claim.status === 'settled' ? 'bg-emerald-500' :
                            claim.status === 'manager_approved' ? 'bg-teal-500' :
                            claim.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'
                          )} />
                          {claim.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link to={`/app/expense-claims/${claim.id}`}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-700 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-1">
                            <Eye className="w-3.5 h-3.5" /> Details
                          </Link>
                          {isRole('manager', 'admin') && claim.status === 'pending_approval' && (
                            <>
                              <button onClick={() => setApproveModal({ open: true, id: claim.id, comment: 'Approved' })} disabled={!!actionLoading[claim.id]}
                                className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-50" title="Approve">
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                              <button onClick={() => setRejectModal({ open: true, id: claim.id, comment: '' })} disabled={!!actionLoading[claim.id]}
                                className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50" title="Reject">
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {isRole('finance', 'admin') && ['manager_approved', 'finance_approved'].includes(claim.status) && (
                            <button onClick={() => handleSettleClick(claim.id)} disabled={!!actionLoading[claim.id]}
                              className="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1 shadow-sm transition-colors"
                              title="Mark as Settled / Reimbursed">
                              <Banknote className="w-3.5 h-3.5" /> Settle
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approve Modal */}
      <Dialog open={approveModal.open} onOpenChange={open => setApproveModal(p => ({ ...p, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold text-slate-900">Approve Expense Claim</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Optionally add an approval comment for the employee.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Approval Comment</label>
              <input
                type="text"
                value={approveModal.comment}
                onChange={e => setApproveModal(p => ({ ...p, comment: e.target.value }))}
                placeholder="Approved..."
                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              onClick={() => setApproveModal(p => ({ ...p, open: false }))}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApproveSubmit}
              disabled={!!actionLoading[approveModal.id]}
              className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
            >
              Approve Claim
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModal.open} onOpenChange={open => setRejectModal(p => ({ ...p, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold text-slate-900">Reject Expense Claim</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Please state the reason for rejecting this expense claim (at least 5 characters).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Rejection Reason *</label>
              <textarea
                rows={3}
                value={rejectModal.comment}
                onChange={e => setRejectModal(p => ({ ...p, comment: e.target.value }))}
                placeholder="Reason for rejection..."
                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              onClick={() => setRejectModal(p => ({ ...p, open: false }))}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRejectSubmit}
              disabled={!!actionLoading[rejectModal.id] || !rejectModal.comment.trim() || rejectModal.comment.trim().length < 5}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
            >
              Confirm Rejection
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settle Confirmation Dialog */}
      <AlertDialog open={showSettleModal} onOpenChange={setShowSettleModal}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <div className="w-11 h-11 rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-1 mx-auto sm:mx-0">
              <Banknote className="w-5 h-5" />
            </div>
            <AlertDialogTitle className="text-lg sm:text-xl font-bold text-slate-900">
              Settle Expense Claim
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              Are you sure you want to mark this expense claim as settled/reimbursed? The employee will be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-2 sm:gap-0">
            <AlertDialogCancel
              onClick={() => setShowSettleModal(false)}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSettle}
              className="bg-green-600 hover:bg-green-700 text-white font-medium"
            >
              Mark as Settled
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
